// src/pages/CourseHome.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

export default function CourseHome() {
  const { alias } = useParams();              // e.g. "frm2"
  const [loading, setLoading] = useState(true);
  const [courseRow, setCourseRow] = useState(null); // {name, alias}
  const [topics, setTopics] = useState([]);
  const [filters, setFilters] = useState({ level0: '', level1: '', level2: '' });
  const [error, setError] = useState('');

  // 1) Resolve alias -> course name
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setCourseRow(null);
      setTopics([]);

      const { data: c, error: ce } = await supabase
        .from('courses')
        .select('name, alias')
        .eq('alias', alias)
        .maybeSingle();

      if (ce) {
        if (!cancelled) {
          setError('Failed to look up course.');
          setLoading(false);
        }
        return;
      }
      if (!c) {
        if (!cancelled) {
          setError('Course not found.');
          setLoading(false);
        }
        return;
      }

      if (!cancelled) setCourseRow(c);

      // 2) Load content for the resolved course name
      const { data: rows, error: te } = await supabase
        .from('subtopic_summary')
        .select('*')
        .eq('course', c.name);

      if (te) {
        if (!cancelled) {
          setError('Failed to load course content.');
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setTopics(rows || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [alias]);

  // Build dropdown options
  const options = useMemo(() => {
    const level0 = [...new Set(topics.map(t => t.level0_name))];
    const level1 = [...new Set(topics.map(t => t.topic_name))];
    const level2 = [...new Set(topics.map(t => t.module_name))];
    return { level0, level1, level2 };
  }, [topics]);

  // Filtered rows
  const filtered = useMemo(() => {
    let f = topics;
    if (filters.level0) f = f.filter(r => r.level0_name === filters.level0);
    if (filters.level1) f = f.filter(r => r.topic_name === filters.level1);
    if (filters.level2) f = f.filter(r => r.module_name === filters.level2);
    return f;
  }, [topics, filters]);

  if (loading) return <div className="p-6">Loading…</div>;
  if (error) return (
    <div className="p-6">
      <div className="text-red-600 mb-3">{error}</div>
      <Link className="text-blue-600 underline" to="/dashboard">Go back</Link>
    </div>
  );
  if (!courseRow) return null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-2xl font-bold">
          {courseRow.name} <span className="text-gray-500 text-base">(/{courseRow.alias})</span>
        </h1>
        <Link to="/dashboard" className="text-sm text-blue-600 underline">Dashboard</Link>
      </div>

      {topics.length === 0 ? (
        <div className="text-gray-600">No content found for this course yet.</div>
      ) : (
        <>
          {/* Small filter bar */}
          <div className="flex flex-wrap gap-3 mb-4">
            <select
              className="border p-2 rounded"
              value={filters.level0}
              onChange={(e) => setFilters(prev => ({ ...prev, level0: e.target.value }))}
            >
              <option value="">All Level 0</option>
              {options.level0.map(v => <option key={v} value={v}>{v}</option>)}
            </select>

            <select
              className="border p-2 rounded"
              value={filters.level1}
              onChange={(e) => setFilters(prev => ({ ...prev, level1: e.target.value }))}
            >
              <option value="">All Level 1</option>
              {options.level1.map(v => <option key={v} value={v}>{v}</option>)}
            </select>

            <select
              className="border p-2 rounded"
              value={filters.level2}
              onChange={(e) => setFilters(prev => ({ ...prev, level2: e.target.value }))}
            >
              <option value="">All Level 2</option>
              {options.level2.map(v => <option key={v} value={v}>{v}</option>)}
            </select>

            <button
              className="border px-3 py-2 rounded"
              onClick={() => setFilters({ level0: '', level1: '', level2: '' })}
            >
              Reset filters
            </button>
          </div>

          {/* Simple content table from subtopic_summary */}
          <table className="table-auto w-full border">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 border">Level 0</th>
                <th className="px-3 py-2 border">Level 1 (Topic)</th>
                <th className="px-3 py-2 border">Level 2 (Module)</th>
                <th className="px-3 py-2 border">Level 3 (Outline)</th>
                <th className="px-3 py-2 border">Module Code</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.outline_id}>
                  <td className="px-3 py-2 border">{r.level0_name}</td>
                  <td className="px-3 py-2 border">{r.topic_name}</td>
                  <td className="px-3 py-2 border">{r.module_name}</td>
                  <td className="px-3 py-2 border">{r.outline_name}</td>
                  <td className="px-3 py-2 border">{r.module_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
